import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../store';
import useTranslations from '../../../hooks/useTranslations';
import useGetGridData from '../../../hooks/useGetGridData';
import useUrlSearchParam from '../../../hooks/useUrlSearchParams';
import { FieldTypes, IFormFields } from '../../../models/form';
import Filters from '../../../components/Container/Filters/Filters';
import EnhancedTable from '../../../components/UI/RcTable/EnhancedTable';
import TableButtons from '../../../components/UI/TableButtons/TableButtons';
import ActionButtons from '../../../components/UI/ActionButtons/ActionButtons';
import Title from '../../../components/UI/Title/Title';
import { SubCustomersRequestsActions } from '../../../store/entities/subCustomersRequests/subCustomersRequests.actions';
import { IPage } from '../../../models/page';
import { TableActions } from '../../../store/entities/table/table.actions';
import { getDateTimeAndSeconds, sortDataByDate } from '../../../utils/formatters';
import { ISubCustomersRequest } from '../../../models/subCustomersRequest';
import { subCustomersRequestsListSearchValidation } from '../../../utils/formValidation/subCustomersRequestsList';
import { PERMISSIONS } from '../../../utils/permissions';
import { CONSTANTS } from '../../../utils/constants';
import { SUB_CUSTOMERS_REQUEST_STATUS_OPTIONS } from '../subCustomersRequestsConstants';

type SubCustomersRequestRow = {
  id: number;
  requestId: number;
  primaryCustomerName: string;
  primaryCustomerPhoneNumber: string;
  subCustomerPhoneNumber: string;
  requestStatus: string;
  created: string;
  lastModified: string;
  actions: React.ReactElement;
};

type SubCustomersRequestColumn = {
  id: string;
  numeric: boolean;
  disablePadding: boolean;
  label: string;
};

const SubCustomersRequestsList = ({ permission, nextRoute }: IPage) => {
  const [rows, setRows] = useState<SubCustomersRequestRow[]>([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { rcTranslate } = useTranslations();
  const { onPageChange } = useGetGridData();
  const { getValueFromQuery, params } = useUrlSearchParam();

  const { subCustomersRequests } = useAppSelector((state) => state.subCustomersRequests);
  const { nextPageNumber, currentPage, sortModel } = useAppSelector((state) => state.table);
  const { formikProps } = useAppSelector((state) => state.form);
  const { appPermissions }: { appPermissions: string[] } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (permission === false) {
      navigate(nextRoute);
    }
  }, [permission, nextRoute, navigate]);

  useEffect(() => {
    if (permission) {
      if (currentPage !== 1) {
        dispatch(TableActions.setTableData({ currentPage: 1 }));
      }
      dispatch(SubCustomersRequestsActions.getSubCustomersRequests(params));
    }
  }, [dispatch, params, permission]);

  useEffect(() => {
    if (nextPageNumber) {
      const getData = async () => {
        dispatch(
          SubCustomersRequestsActions.getSubCustomersRequests(
            params,
            sortModel,
            false,
            nextPageNumber
          )
        );
        await dispatch(TableActions.setTableData({ nextPageNumber: null }));
      };
      getData();
    }
  }, [dispatch, nextPageNumber, sortModel]);

  useEffect(() => {
    if (sortModel.length) {
      const getData = async () => {
        dispatch(SubCustomersRequestsActions.getSubCustomersRequests(null, sortModel, false));
        await dispatch(TableActions.setTableData({ nextPageNumber: null }));
      };
      getData();
    }
  }, [sortModel]);

  const columns: SubCustomersRequestColumn[] = useMemo(
    () => [
      {
        id: 'requestId',
        numeric: true,
        disablePadding: true,
        label: rcTranslate('subCustomersRequests.table.id'),
      },
      {
        id: 'primaryCustomerName',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('subCustomersRequests.table.primaryCustomerName'),
      },
      {
        id: 'primaryCustomerPhoneNumber',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('subCustomersRequests.table.primaryCustomerPhoneNumber'),
      },
      {
        id: 'subCustomerPhoneNumber',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('subCustomersRequests.table.subCustomerPhoneNumber'),
      },
      {
        id: 'requestStatus',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('subCustomersRequests.table.requestStatus'),
      },
      {
        id: 'created',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('subCustomersRequests.table.created'),
      },
      {
        id: 'lastModified',
        numeric: false,
        disablePadding: true,
        label: rcTranslate('subCustomersRequests.table.lastModified'),
      },
      {
        id: 'actions',
        numeric: false,
        disablePadding: true,
        label: '',
      },
    ],
    [rcTranslate]
  );

  const openEdit = (id: number) => {
    navigate(
      `/${CONSTANTS.ROUTES.SUB_CUSTOMERS_MANAGEMENT.BASE}/${CONSTANTS.ROUTES.SUB_CUSTOMERS_MANAGEMENT.CHILDREN.SUB_CUSTOMERS_UPDATE}/${id}`
    );
  };

  const mapSubCustomersRequestsToTableRows = useCallback(
    (items: ISubCustomersRequest[]) => {
      return items.map((item: ISubCustomersRequest) => {
        return {
          id: item.id,
          requestId: item.id,
          primaryCustomerName: item.primaryCustomerName ?? '',
          primaryCustomerPhoneNumber: item.primaryCustomerPhoneNumber ?? '',
          subCustomerPhoneNumber: item.subCustomerPhoneNumber ?? '',
          requestStatus: item.requestStatus != null
            ? rcTranslate(`subCustomersRequests.statuses.${item.requestStatus}`)
            : '',
          created: item.created ? getDateTimeAndSeconds(item.created) : '',
          lastModified: item.lastModified ? getDateTimeAndSeconds(item.lastModified) : '',
          actions: (
            <ActionButtons
              menuItems={[
                {
                  label: 'subCustomersRequests.actions.edit',
                  onClick: () => openEdit(item.id),
                  show: appPermissions.includes(PERMISSIONS.UPDATE_SUB_CUSTOMER_REQUEST),
                },
              ]}
              hide={!appPermissions.includes(PERMISSIONS.UPDATE_SUB_CUSTOMER_REQUEST)}
            />
          ),
        };
      });
    },
    [rcTranslate, appPermissions]
  );

  useEffect(() => {
    const rows = subCustomersRequests?.length
      ? mapSubCustomersRequestsToTableRows(subCustomersRequests)
      : [];
    setRows(rows);
  }, [subCustomersRequests, mapSubCustomersRequestsToTableRows]);

  const searchFields: IFormFields = {
    initialValues: {
      primaryCustomerPhoneNumber: getValueFromQuery('primaryCustomerPhoneNumber'),
      subCustomerPhoneNumber: getValueFromQuery('subCustomerPhoneNumber'),
      statuses: getValueFromQuery('statuses'),
      from: getValueFromQuery('from'),
      to: getValueFromQuery('to'),
    },
    formData: [
      {
        type: FieldTypes.CHILDREN,
        id: 'externalFieldsWrapper',
        fields: [
          {
            type: FieldTypes.TEL,
            id: 'primaryCustomerPhoneNumber',
            label: 'subCustomersRequests.filters.primaryCustomerPhoneNumber',
            props: { inputProps: { maxLength: 14 } },
          },
          {
            type: FieldTypes.TEL,
            id: 'subCustomerPhoneNumber',
            label: 'subCustomersRequests.filters.subCustomerPhoneNumber',
            props: { inputProps: { maxLength: 14 } },
          },
          {
            type: FieldTypes.SELECT,
            id: 'statuses',
            label: 'subCustomersRequests.filters.status',
            items: SUB_CUSTOMERS_REQUEST_STATUS_OPTIONS,
          },
          {
            type: FieldTypes.DATE,
            id: 'from',
            label: 'subCustomersRequests.filters.from',
            props: { getStartOfDay: true },
          },
          {
            type: FieldTypes.DATE,
            id: 'to',
            label: 'subCustomersRequests.filters.to',
            props: {
              getFullDayTime: true,
              numOfDays: 1,
              minDate: new Date(formikProps?.values?.from),
            },
          },
        ],
      },
    ],
  };

  return (
    <div className="pageContainer">
      <Title text="subCustomersRequests.pageTitle" />

      <Filters fields={searchFields} filtersValidation={subCustomersRequestsListSearchValidation} showExtendedSearchButton={false} />

      <TableButtons generalButtons={[]} tableButtons={[]} />

      <div className="subCustomersRequestsList">
        <EnhancedTable
          rows={rows}
          columns={columns}
          sortDataByDate={sortDataByDate}
          checkboxSelection={false}
          expandable={false}
          showId={false}
        />
      </div>
    </div>
  );
};

export default SubCustomersRequestsList;
